"use client";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "./chunk-XVLNMKQN.js";
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from "./chunk-OGYXIAZG.js";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "./chunk-LZOXHYKW.js";
import {
  Separator
} from "./chunk-2RSWJI76.js";
import {
  Skeleton
} from "./chunk-ZGMHLUNY.js";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from "./chunk-66TNYVR5.js";
import {
  Input
} from "./chunk-3MTG4OZ6.js";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator
} from "./chunk-AW7BJHZO.js";
import {
  Button
} from "./chunk-24TR2GH3.js";
import {
  Badge
} from "./chunk-V2FJPXBR.js";

// src/data-table.tsx
import { cn, range } from "@momo/utils";
import { flexRender } from "@tanstack/react-table";
import {
  ArrowDownIcon,
  ArrowUpIcon,
  CheckIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronsLeftIcon,
  ChevronsRightIcon,
  ChevronsUpDownIcon,
  EyeOffIcon,
  PlusCircleIcon,
  Settings2Icon,
  XIcon
} from "lucide-react";
import { useMemo } from "react";
import { Fragment, jsx, jsxs } from "react/jsx-runtime";
var DataTable = (props) => {
  const { table, children, ...rest } = props;
  return /* @__PURE__ */ jsxs("div", { className: "w-full space-y-2.5", ...rest, children: [
    children,
    /* @__PURE__ */ jsx("div", { className: "rounded-md border", children: /* @__PURE__ */ jsxs(Table, { children: [
      /* @__PURE__ */ jsx(TableHeader, { children: table.getHeaderGroups().map((headerGroup) => /* @__PURE__ */ jsx(TableRow, { children: headerGroup.headers.map((header) => {
        return /* @__PURE__ */ jsx(TableHead, { children: header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext()) }, header.id);
      }) }, headerGroup.id)) }),
      /* @__PURE__ */ jsx(TableBody, { children: table.getRowModel().rows.length > 0 ? table.getRowModel().rows.map((row) => /* @__PURE__ */ jsx(TableRow, { "data-state": row.getIsSelected() && "selected", children: row.getVisibleCells().map((cell) => /* @__PURE__ */ jsx(TableCell, { children: flexRender(cell.column.columnDef.cell, cell.getContext()) }, cell.id)) }, row.id)) : /* @__PURE__ */ jsx(TableRow, { children: /* @__PURE__ */ jsx(TableCell, { colSpan: table.getAllColumns().length, className: "h-24 text-center", children: "No results." }) }) })
    ] }) }),
    /* @__PURE__ */ jsx("div", { className: "flex flex-col gap-2.5", children: /* @__PURE__ */ jsx(DataTablePagination, { table }) })
  ] });
};
var defaultPageSizeOptions = [10, 20, 30, 40, 50];
var DataTablePagination = (props) => {
  const { table, pageSizeOptions = defaultPageSizeOptions } = props;
  return /* @__PURE__ */ jsxs("div", { className: "flex w-full flex-col-reverse items-center justify-between gap-4 overflow-auto p-1 sm:flex-row sm:gap-8", children: [
    /* @__PURE__ */ jsxs("div", { className: "text-muted-foreground flex-1 whitespace-nowrap text-sm", children: [
      table.getFilteredSelectedRowModel().rows.length,
      " of",
      " ",
      table.getFilteredRowModel().rows.length,
      " row(s) selected."
    ] }),
    /* @__PURE__ */ jsxs("div", { className: "flex flex-col-reverse items-center gap-4 sm:flex-row sm:gap-6 lg:gap-8", children: [
      /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
        /* @__PURE__ */ jsx("p", { className: "whitespace-nowrap text-sm font-medium", children: "Rows per page" }),
        /* @__PURE__ */ jsxs(
          Select,
          {
            value: `${table.getState().pagination.pageSize}`,
            onValueChange: (value) => {
              table.setPageSize(Number(value));
            },
            children: [
              /* @__PURE__ */ jsx(SelectTrigger, { className: "h-8 w-[4.5rem]", children: /* @__PURE__ */ jsx(SelectValue, { placeholder: table.getState().pagination.pageSize }) }),
              /* @__PURE__ */ jsx(SelectContent, { side: "top", children: pageSizeOptions.map((pageSize) => /* @__PURE__ */ jsx(SelectItem, { value: `${pageSize}`, children: pageSize }, pageSize)) })
            ]
          }
        )
      ] }),
      /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-center text-sm font-medium", children: [
        "Page ",
        table.getState().pagination.pageIndex + 1,
        " of ",
        table.getPageCount()
      ] }),
      /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
        /* @__PURE__ */ jsx(
          Button,
          {
            "aria-label": "Go to first page",
            variant: "outline",
            className: "hidden size-8 p-0 lg:flex",
            onClick: () => {
              table.setPageIndex(0);
            },
            disabled: !table.getCanPreviousPage(),
            children: /* @__PURE__ */ jsx(ChevronsLeftIcon, { className: "size-4", "aria-hidden": "true" })
          }
        ),
        /* @__PURE__ */ jsx(
          Button,
          {
            "aria-label": "Go to previous page",
            variant: "outline",
            size: "icon",
            className: "size-8",
            onClick: () => {
              table.previousPage();
            },
            disabled: !table.getCanPreviousPage(),
            children: /* @__PURE__ */ jsx(ChevronLeftIcon, { className: "size-4", "aria-hidden": "true" })
          }
        ),
        /* @__PURE__ */ jsx(
          Button,
          {
            "aria-label": "Go to next page",
            variant: "outline",
            size: "icon",
            className: "size-8",
            onClick: () => {
              table.nextPage();
            },
            disabled: !table.getCanNextPage(),
            children: /* @__PURE__ */ jsx(ChevronRightIcon, { className: "size-4", "aria-hidden": "true" })
          }
        ),
        /* @__PURE__ */ jsx(
          Button,
          {
            "aria-label": "Go to last page",
            variant: "outline",
            size: "icon",
            className: "hidden size-8 lg:flex",
            onClick: () => {
              table.setPageIndex(table.getPageCount() - 1);
            },
            disabled: !table.getCanNextPage(),
            children: /* @__PURE__ */ jsx(ChevronsRightIcon, { className: "size-4", "aria-hidden": "true" })
          }
        )
      ] })
    ] })
  ] });
};
var DataTableColumnHeader = (props) => {
  const { column, title, className } = props;
  if (!column.getCanSort()) {
    return /* @__PURE__ */ jsx("div", { className: cn(className), children: title });
  }
  const isDesc = column.getIsSorted() === "desc";
  const isAsc = column.getIsSorted() === "asc";
  const isUnsorted = !isDesc && !isAsc;
  return /* @__PURE__ */ jsx("div", { className: cn("flex items-center gap-2", className), children: /* @__PURE__ */ jsxs(DropdownMenu, { children: [
    /* @__PURE__ */ jsx(DropdownMenuTrigger, { asChild: true, children: /* @__PURE__ */ jsxs(Button, { variant: "ghost", size: "sm", className: "data-[state=open]:bg-accent -ml-3 h-8", children: [
      /* @__PURE__ */ jsx("span", { children: title }),
      isDesc ? /* @__PURE__ */ jsx(ArrowDownIcon, { className: "ml-2.5 size-4" }) : null,
      isAsc ? /* @__PURE__ */ jsx(ArrowUpIcon, { className: "ml-2.5 size-4" }) : null,
      isUnsorted ? /* @__PURE__ */ jsx(ChevronsUpDownIcon, { className: "ml-2.5 size-4" }) : null
    ] }) }),
    /* @__PURE__ */ jsxs(DropdownMenuContent, { align: "start", children: [
      /* @__PURE__ */ jsxs(
        DropdownMenuItem,
        {
          onClick: () => {
            column.toggleSorting(false);
          },
          children: [
            /* @__PURE__ */ jsx(ArrowUpIcon, { className: "text-muted-foreground/70 mr-2 size-3.5" }),
            "Asc"
          ]
        }
      ),
      /* @__PURE__ */ jsxs(
        DropdownMenuItem,
        {
          onClick: () => {
            column.toggleSorting(true);
          },
          children: [
            /* @__PURE__ */ jsx(ArrowDownIcon, { className: "text-muted-foreground/70 mr-2 size-3.5" }),
            "Desc"
          ]
        }
      ),
      /* @__PURE__ */ jsx(DropdownMenuSeparator, {}),
      /* @__PURE__ */ jsxs(
        DropdownMenuItem,
        {
          onClick: () => {
            column.toggleVisibility(false);
          },
          children: [
            /* @__PURE__ */ jsx(EyeOffIcon, { className: "text-muted-foreground/70 mr-2 size-3.5" }),
            "Hide"
          ]
        }
      )
    ] })
  ] }) });
};
var DataTableViewOptions = (props) => {
  const { table } = props;
  return /* @__PURE__ */ jsxs(DropdownMenu, { children: [
    /* @__PURE__ */ jsx(DropdownMenuTrigger, { asChild: true, children: /* @__PURE__ */ jsxs(Button, { variant: "outline", size: "sm", className: "ml-auto hidden h-8 lg:flex", children: [
      /* @__PURE__ */ jsx(Settings2Icon, { className: "mr-2 size-4" }),
      "View"
    ] }) }),
    /* @__PURE__ */ jsxs(DropdownMenuContent, { align: "end", className: "w-[150px]", children: [
      /* @__PURE__ */ jsx(DropdownMenuLabel, { children: "Toggle columns" }),
      /* @__PURE__ */ jsx(DropdownMenuSeparator, {}),
      table.getAllColumns().filter((column) => column.accessorFn !== void 0 && column.getCanHide()).map((column) => {
        return /* @__PURE__ */ jsx(
          DropdownMenuCheckboxItem,
          {
            className: "capitalize",
            checked: column.getIsVisible(),
            onCheckedChange: (value) => {
              column.toggleVisibility(!!value);
            },
            children: column.id
          },
          column.id
        );
      })
    ] })
  ] });
};
var DataTableFacetedFilter = (props) => {
  const { column, title, options } = props;
  const facets = column?.getFacetedUniqueValues();
  const selectedValues = new Set(column?.getFilterValue());
  return /* @__PURE__ */ jsxs(Popover, { children: [
    /* @__PURE__ */ jsx(PopoverTrigger, { asChild: true, children: /* @__PURE__ */ jsxs(Button, { variant: "outline", size: "sm", className: "h-8 border-dashed", children: [
      /* @__PURE__ */ jsx(PlusCircleIcon, { className: "mr-2 size-4" }),
      title,
      selectedValues.size > 0 && /* @__PURE__ */ jsxs(Fragment, { children: [
        /* @__PURE__ */ jsx(Separator, { orientation: "vertical", className: "mx-2 h-4" }),
        /* @__PURE__ */ jsx(Badge, { variant: "secondary", className: "rounded-sm px-1 font-normal lg:hidden", children: selectedValues.size }),
        /* @__PURE__ */ jsx("div", { className: "hidden space-x-1 lg:flex", children: selectedValues.size > 2 ? /* @__PURE__ */ jsxs(Badge, { variant: "secondary", className: "rounded-sm px-1 font-normal", children: [
          selectedValues.size,
          " selected"
        ] }) : options.filter((option) => selectedValues.has(option.value)).map((option) => /* @__PURE__ */ jsx(
          Badge,
          {
            variant: "secondary",
            className: "rounded-sm px-1 font-normal",
            children: option.label
          },
          option.value
        )) })
      ] })
    ] }) }),
    /* @__PURE__ */ jsx(PopoverContent, { className: "w-[200px] p-0", align: "start", children: /* @__PURE__ */ jsxs(Command, { children: [
      /* @__PURE__ */ jsx(CommandInput, { placeholder: title }),
      /* @__PURE__ */ jsxs(CommandList, { children: [
        /* @__PURE__ */ jsx(CommandEmpty, { children: "No results found." }),
        /* @__PURE__ */ jsx(CommandGroup, { children: options.map((option) => {
          const isSelected = selectedValues.has(option.value);
          return /* @__PURE__ */ jsxs(
            CommandItem,
            {
              onSelect: () => {
                if (isSelected) {
                  selectedValues.delete(option.value);
                } else {
                  selectedValues.add(option.value);
                }
                const filterValues = [...selectedValues];
                column?.setFilterValue(filterValues.length > 0 ? filterValues : void 0);
              },
              children: [
                /* @__PURE__ */ jsx(
                  "div",
                  {
                    className: cn(
                      "border-primary mr-2 flex size-4 items-center justify-center rounded-sm border",
                      isSelected ? "bg-primary text-primary-foreground" : "opacity-50 [&_svg]:invisible"
                    ),
                    children: /* @__PURE__ */ jsx(CheckIcon, {})
                  }
                ),
                option.icon && /* @__PURE__ */ jsx(option.icon, { className: "text-muted-foreground mr-2 size-4" }),
                /* @__PURE__ */ jsx("span", { children: option.label }),
                facets?.get(option.value) !== void 0 && /* @__PURE__ */ jsx("span", { className: "ml-auto flex size-4 items-center justify-center font-mono text-xs", children: facets.get(option.value) })
              ]
            },
            option.value
          );
        }) }),
        selectedValues.size > 0 && /* @__PURE__ */ jsxs(Fragment, { children: [
          /* @__PURE__ */ jsx(CommandSeparator, {}),
          /* @__PURE__ */ jsx(CommandGroup, { children: /* @__PURE__ */ jsx(
            CommandItem,
            {
              onSelect: () => column?.setFilterValue(void 0),
              className: "justify-center text-center",
              children: "Clear filters"
            }
          ) })
        ] })
      ] })
    ] }) })
  ] });
};
var defaultFilterFields = [];
var DataTableToolbar = (props) => {
  const { table, filterFields = defaultFilterFields } = props;
  const { searchableColumns, filterableColumns } = useMemo(() => {
    return {
      searchableColumns: filterFields.filter((field) => !field.options),
      filterableColumns: filterFields.filter((field) => field.options)
    };
  }, [filterFields]);
  const isFiltered = table.getState().columnFilters.length > 0;
  return /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-between", children: [
    /* @__PURE__ */ jsxs("div", { className: "flex flex-1 items-center gap-2", children: [
      searchableColumns.length > 0 && searchableColumns.map(
        (column) => table.getColumn(column.id ? String(column.id) : "") && /* @__PURE__ */ jsx(
          Input,
          {
            placeholder: column.placeholder,
            value: table.getColumn(String(column.id))?.getFilterValue() ?? "",
            onChange: (event) => table.getColumn(String(column.id))?.setFilterValue(event.target.value),
            className: "h-8 w-40 lg:w-64"
          },
          String(column.id)
        )
      ),
      filterableColumns.length > 0 && filterableColumns.map(
        (column) => table.getColumn(column.id ? String(column.id) : "") && /* @__PURE__ */ jsx(
          DataTableFacetedFilter,
          {
            column: table.getColumn(column.id ? String(column.id) : ""),
            title: column.label,
            options: column.options ?? []
          },
          String(column.id)
        )
      ),
      isFiltered && /* @__PURE__ */ jsxs(
        Button,
        {
          variant: "ghost",
          className: "h-8 px-2 lg:px-3",
          onClick: () => {
            table.resetColumnFilters();
          },
          children: [
            "Reset",
            /* @__PURE__ */ jsx(XIcon, { className: "ml-2 size-4", "aria-hidden": "true" })
          ]
        }
      )
    ] }),
    /* @__PURE__ */ jsx(DataTableViewOptions, { table })
  ] });
};
var defaultCellWidths = ["auto"];
var DataTableSkeleton = (props) => {
  const {
    columnCount,
    rowCount = 10,
    searchableColumnsCount = 0,
    filterableColumnCount = 0,
    cellWidths = defaultCellWidths,
    className,
    ...rest
  } = props;
  return /* @__PURE__ */ jsxs("div", { className: cn("w-full space-y-2.5", className), ...rest, children: [
    /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-between", children: [
      /* @__PURE__ */ jsxs("div", { className: "flex flex-1 items-center gap-2", children: [
        searchableColumnsCount > 0 && range(searchableColumnsCount).map((i) => /* @__PURE__ */ jsx(Skeleton, { className: "h-8 w-40 lg:w-60" }, i)),
        filterableColumnCount > 0 && range(filterableColumnCount).map((i) => /* @__PURE__ */ jsx(Skeleton, { className: "h-8 w-[4.5rem] border-dashed" }, i))
      ] }),
      /* @__PURE__ */ jsx(Skeleton, { className: "ml-auto hidden h-8 w-[4.5rem] lg:flex" })
    ] }),
    /* @__PURE__ */ jsx("div", { className: "rounded-md border", children: /* @__PURE__ */ jsxs(Table, { children: [
      /* @__PURE__ */ jsx(TableHeader, { children: range(1).map((i) => /* @__PURE__ */ jsx(TableRow, { className: "hover:bg-transparent", children: range(columnCount).map((j) => /* @__PURE__ */ jsx(
        TableHead,
        {
          style: {
            width: cellWidths[j],
            minWidth: cellWidths[j]
          },
          children: /* @__PURE__ */ jsx(Skeleton, { className: "h-6 w-full" })
        },
        j
      )) }, i)) }),
      /* @__PURE__ */ jsx(TableBody, { children: range(rowCount).map((i) => /* @__PURE__ */ jsx(TableRow, { className: "hover:bg-transparent", children: range(columnCount).map((j) => /* @__PURE__ */ jsx(
        TableCell,
        {
          style: {
            width: cellWidths[j],
            minWidth: cellWidths[j]
          },
          children: /* @__PURE__ */ jsx(Skeleton, { className: "h-6 w-full" })
        },
        j
      )) }, i)) })
    ] }) }),
    /* @__PURE__ */ jsxs("div", { className: "flex w-full flex-col-reverse items-center justify-between gap-4 overflow-auto p-1 sm:flex-row sm:gap-8", children: [
      /* @__PURE__ */ jsx(Skeleton, { className: "h-7 w-40 shrink-0" }),
      /* @__PURE__ */ jsxs("div", { className: "flex flex-col-reverse items-center gap-4 sm:flex-row sm:gap-6 lg:gap-8", children: [
        /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
          /* @__PURE__ */ jsx(Skeleton, { className: "h-7 w-24" }),
          /* @__PURE__ */ jsx(Skeleton, { className: "h-7 w-[4.5rem]" })
        ] }),
        /* @__PURE__ */ jsx("div", { className: "flex items-center justify-center text-sm font-medium", children: /* @__PURE__ */ jsx(Skeleton, { className: "h-7 w-20" }) }),
        /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
          /* @__PURE__ */ jsx(Skeleton, { className: "hidden size-7 lg:block" }),
          /* @__PURE__ */ jsx(Skeleton, { className: "size-7" }),
          /* @__PURE__ */ jsx(Skeleton, { className: "size-7" }),
          /* @__PURE__ */ jsx(Skeleton, { className: "hidden size-7 lg:block" })
        ] })
      ] })
    ] })
  ] });
};

export {
  DataTable,
  DataTableColumnHeader,
  DataTableViewOptions,
  DataTableFacetedFilter,
  DataTableToolbar,
  DataTableSkeleton
};
